import { CameraControls } from '@react-three/drei';
import { memo, type RefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { BoxHelper, EdgesGeometry, type Group, type Mesh } from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { useShallow } from 'zustand/shallow';

import { ArticleEdgeLines } from 'app/modules/wallforming/components/scenes/objects/articles/ArticleEdgeLines';
import { ArticleInsertionPoints } from 'app/modules/wallforming/components/scenes/objects/articles/ArticleInsertionPoints';
import { getMeshLodLevel } from 'app/modules/wallforming/components/scenes/objects/articles/utils/batchedMeshUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

import type { PrefabModel } from 'api/models';
import type { ArticleMesh } from 'app/modules/wallforming/components/scenes/objects/articles/types/articleMesh';

const IS_DEV_TOOLS = import.meta.env.VITE_DEV_TOOLS === 'true';

interface ArticlePreviewMeshProps {
    articleId: string;
    articleMeshes: ArticleMesh[];
    articleRepresentation: PrefabModel;
    previewCameraControlsRef: RefObject<CameraControls | null>;
    previewHelperRef: RefObject<BoxHelper | undefined>;
    restorePreviewCamera: () => boolean;
}

export const ArticlePreviewMesh = memo(
    ({
        articleId,
        articleMeshes,
        articleRepresentation,
        previewCameraControlsRef,
        previewHelperRef,
        restorePreviewCamera
    }: ArticlePreviewMeshProps) => {
        const articleMeshRefs = useRef<Mesh[]>([]);

        const { edgeGeometries, fitToObject, edgeLineThickness } = usePlacementStore(
            useShallow((state) => ({
                edgeGeometries: state.articles[articleId]?.edgeGeometries ?? null,
                fitToObject: state.fitToObject,
                edgeLineThickness: state.graphicsSettings.edgeLineThickness
            }))
        );

        const glbVisible = usePlacementStore((state) => (IS_DEV_TOOLS ? state.glbVisible : true));
        const meshVisibility = usePlacementStore((state) => (IS_DEV_TOOLS ? state.meshVisibility : {}));
        const previewMeshes = useMemo(
            () => articleMeshes.filter((mesh) => getMeshLodLevel(mesh.name) === 0),
            [articleMeshes]
        );

        const articleEdgesGeometry = useMemo(() => {
            if (!edgeGeometries) {
                return undefined;
            }

            const partGeometries = Object.values(edgeGeometries)
                .map((lodGeometries) => lodGeometries[0] ?? lodGeometries.find((geometry) => geometry !== null) ?? null)
                .filter((geometry) => geometry !== null);
            if (partGeometries.length === 0) {
                return undefined;
            }

            const merged = partGeometries.length === 1 ? partGeometries[0] : mergeGeometries(partGeometries);
            return merged ? new EdgesGeometry(merged, 90) : undefined;
        }, [edgeGeometries]);

        const handleArticlePreviewGroupRef = useCallback(
            (group: Group | null) => {
                if (group && articleMeshes.length > 0) {
                    previewHelperRef.current = new BoxHelper(group, 0xffff00);

                    if (previewHelperRef.current && previewCameraControlsRef.current && !restorePreviewCamera()) {
                        fitToObject(previewHelperRef.current, previewCameraControlsRef, true, true);
                    }
                }
            },
            [articleMeshes, fitToObject, previewCameraControlsRef, previewHelperRef, restorePreviewCamera]
        );

        useEffect(() => {
            const helper = previewHelperRef;
            return () => {
                articleEdgesGeometry?.dispose();
                helper.current?.geometry.dispose();
                articleMeshes.forEach((meshData) => {
                    meshData.geometry?.dispose();
                    meshData.material?.dispose();
                });
                articleMeshRefs.current = [];
            };
        }, [articleEdgesGeometry, articleMeshes, previewHelperRef]);

        if (!previewMeshes || previewMeshes.length === 0 || !articleEdgesGeometry) {
            return null;
        }

        return (
            <>
                <group ref={handleArticlePreviewGroupRef} visible={glbVisible}>
                    {previewMeshes.map(
                        (meshData, meshIndex) =>
                            meshData.name !== 'Selection' && (
                                <mesh
                                    key={`${meshData.id}`}
                                    ref={(mesh: Mesh | null) => {
                                        if (mesh) {
                                            articleMeshRefs.current[meshIndex] = mesh;
                                        }
                                    }}
                                    geometry={meshData.geometry}
                                    material={meshData.material}
                                    name={meshData.name}
                                    visible={meshVisibility[meshData.id] !== false}
                                />
                            )
                    )}
                </group>

                <ArticleEdgeLines
                    color={0x777777}
                    edgeLineThickness={edgeLineThickness}
                    edgesGeometry={articleEdgesGeometry}
                />

                <ArticleInsertionPoints articleId={articleId} articleRepresentation={articleRepresentation} />
            </>
        );
    }
);

ArticlePreviewMesh.displayName = 'ArticlePreviewMesh';
